/**
 * Auto Parts / Car Catalog Service
 *
 * Uses the shared catalog on Plutoo Hub Storage (jupiter-to-plutoo),
 * with bundled auto-parts-db as fallback (https://github.com/lifeofcapo/car-api).
 *
 * VIN decode uses the free NHTSA vPIC API (car-api has no VIN decoder).
 */

// Use local CJS bridge — package ESM entry breaks CRA babel
import {
  ensurePartsCatalog,
  getBrands,
  getBrand,
  getModelsByBrand,
  getGenerations,
  getGenerationByYear,
  searchModels,
  getParts,
  getPartBySlug,
  searchParts,
  getPartSlugs,
} from './autoPartsDb';
import { getPartCategory, getSupplierLinks } from './partsSupplierLinks';

const norm = (s) => (s || '').toString().trim().toLowerCase();

/** Prefer English slug when part name is Cyrillic (package is bilingual). */
const displayPartName = (part) => {
  const name = (part?.name || '').toString();
  const slug = (part?.slug || '').toString();
  if (/[\u0400-\u04FF]/.test(name) && slug) {
    return slug
      .split('-')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return name || slug || 'Unknown Part';
};

const toPartResult = (part) => {
  if (!part) return null;
  const productName = displayPartName(part);
  const category = getPartCategory(part);
  const links = getSupplierLinks({ ...part, articleProductName: productName });
  return {
    slug: part.slug,
    name: part.name,
    articleNo: part.slug,
    articleNumber: part.slug,
    articleId: part.slug,
    id: part.slug,
    articleProductName: productName,
    productName,
    description: productName,
    supplierName: 'auto-parts-db',
    supplier: 'auto-parts-db',
    categoryName: category,
    category,
    supplierLinks: links,
  };
};

const findBrandName = (make) => {
  const m = norm(make);
  if (!m) return null;
  const brands = getBrands();
  const exact = brands.find((b) => norm(b) === m);
  if (exact) return exact;
  return brands.find((b) => norm(b).includes(m) || m.includes(norm(b))) || null;
};

const findModel = (brandName, model) => {
  if (!brandName) return null;
  const models = getModelsByBrand(brandName);
  const m = norm(model);
  if (!m) return models[0] || null;
  return (
    models.find((mod) => norm(mod.name) === m) ||
    models.find((mod) => norm(mod.name).includes(m) || m.includes(norm(mod.name))) ||
    null
  );
};

/**
 * Free NHTSA VIN decode (car-api has no VIN support).
 */
const decodeVinNhtsa = async (vin) => {
  const cleaned = String(vin || '').trim().toUpperCase();
  if (cleaned.length !== 17) {
    throw new Error('VIN must be exactly 17 characters');
  }
  const url = `https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${encodeURIComponent(cleaned)}?format=json`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`VIN decode failed: ${response.status}`);
  }
  const json = await response.json();
  const row = json?.Results?.[0];
  if (!row) {
    throw new Error('No VIN decode results');
  }
  const make = row.Make || row.Manufacturer || '';
  const model = row.Model || '';
  const year = row.ModelYear || '';
  if (!make && !model) {
    throw new Error(row.ErrorText || 'Could not decode VIN');
  }
  return {
    make,
    manufacturer: make,
    Make: make,
    Manufacturer: make,
    model,
    Model: model,
    year,
    modelYear: year,
    ModelYear: year,
    bodyClass: row.BodyClass,
    vehicleType: row.VehicleType,
    plantCountry: row.PlantCountry,
    raw: row,
  };
};

const withCatalog = (fn) => async (...args) => {
  await ensurePartsCatalog();
  return fn(...args);
};

export const autoPartsService = {
  ensureCatalog: () => ensurePartsCatalog(),

  // ========================================
  // SEARCH (maps to auto-parts-db searchParts)
  // ========================================

  /**
   * Search parts by keyword / slug / article-like term.
   * Returns shape compatible with PartsLookup (articles array).
   */
  searchByArticleNumber: async (articleNo) => {
    if (!articleNo || typeof articleNo !== 'string') {
      throw new Error('Article number is required and must be a string');
    }
    const term = articleNo.trim();
    const bySlug = getPartBySlug(term) || getPartBySlug(norm(term).replace(/\s+/g, '-'));
    if (bySlug) {
      return { articles: [toPartResult(bySlug)] };
    }
    const hits = searchParts(term).map(toPartResult).filter(Boolean);
    return { articles: hits };
  },

  quickArticleSearch: async (articleNo) => {
    return autoPartsService.searchByArticleNumber(articleNo);
  },

  searchByOEMNumber: async (oemNo) => {
    // No OEM data in auto-parts-db — fall back to text/slug search
    return autoPartsService.searchByArticleNumber(oemNo);
  },

  searchEqualOEMNumbers: async () => {
    return [];
  },

  /**
   * Parts are not vehicle-linked in this catalog; returns empty list.
   */
  getCompatibleCarsByArticleNumber: async () => {
    return [];
  },

  // ========================================
  // VIN (NHTSA — not part of car-api)
  // ========================================

  vinDecoderV3: async (vin) => decodeVinNhtsa(vin),
  vinDecoderV2: async (vin) => decodeVinNhtsa(vin),
  vinDecoderV1: async (vin) => decodeVinNhtsa(vin),

  /**
   * Match VIN make/model against local car catalog.
   */
  vinCheck: async (vin) => {
    const decoded = await decodeVinNhtsa(vin);
    const brandName = findBrandName(decoded.make);
    const model = brandName ? findModel(brandName, decoded.model) : null;
    if (!brandName || !model) {
      return { vehicleId: null, ...decoded };
    }
    const year = Number(decoded.year) || null;
    const gen = year
      ? getGenerationByYear(brandName, model.name, year)
      : model.generations?.[0] || null;
    const vehicleId = [brandName, model.name, gen?.name || 'default'].join('|');
    return {
      vehicleId,
      carId: vehicleId,
      id: vehicleId,
      manufacturer: brandName,
      model: model.name,
      generation: gen,
      ...decoded,
    };
  },

  // ========================================
  // BRANDS & MODELS (car-api)
  // ========================================

  listModelsByManufacturer: async (_typeId, manufacturerId) => {
    if (!manufacturerId) {
      throw new Error('Manufacturer ID is required');
    }
    const brandName = findBrandName(manufacturerId) || String(manufacturerId);
    return getModelsByBrand(brandName).map((m) => ({
      modelId: m.name,
      modelSeriesId: m.name,
      id: m.name,
      modelName: m.name,
      name: m.name,
      description: m.name,
      generations: m.generations,
    }));
  },

  getModels: async (_typeId, modelId) => {
    if (!modelId) {
      throw new Error('Model ID is required');
    }
    const hits = searchModels(String(modelId));
    if (!hits.length) return null;
    const { brand, model } = hits[0];
    return {
      modelId: model.name,
      modelName: model.name,
      brand,
      generations: model.generations,
    };
  },

  getVehicleEngineTypes: async (_typeId, modelSeriesId) => {
    if (!modelSeriesId) {
      throw new Error('Model series ID is required');
    }
    // modelSeriesId may be "Brand|Model" or just model name
    const parts = String(modelSeriesId).split('|');
    let brandName;
    let modelName;
    if (parts.length >= 2) {
      brandName = parts[0];
      modelName = parts.slice(1).join('|');
    } else {
      const hits = searchModels(String(modelSeriesId));
      if (!hits.length) return [];
      brandName = hits[0].brand;
      modelName = hits[0].model.name;
    }
    const gens = getGenerations(brandName, modelName) || [];
    return gens.map((g) => ({
      vehicleId: `${brandName}|${modelName}|${g.name}`,
      carId: `${brandName}|${modelName}|${g.name}`,
      id: `${brandName}|${modelName}|${g.name}`,
      name: g.name,
      yearFrom: g.yearFrom,
      yearTo: g.yearTo,
      year: g.yearFrom,
      manufacturer: brandName,
      model: modelName,
    }));
  },

  getCategoryV1: async () => {
    return [
      {
        categoryId: 'all',
        id: 'all',
        productGroupId: 'all',
        categoryName: 'All Parts',
        name: 'All Parts',
        description: 'Full local parts catalog',
      },
    ];
  },

  getCategoryV3: async () => autoPartsService.getCategoryV1(),

  listProductNames: async () => {
    return getParts().map((p) => ({
      name: displayPartName(p),
      productName: displayPartName(p),
      description: displayPartName(p),
      articleNo: p.slug,
      articleNumber: p.slug,
      slug: p.slug,
    }));
  },

  listArticles: async (_vehicleId, categoryId) => {
    const all = getParts().map(toPartResult);
    if (!categoryId || categoryId === 'all') {
      return { articles: all };
    }
    const filtered = searchParts(String(categoryId)).map(toPartResult);
    return { articles: filtered };
  },

  getArticleMedia: async () => {
    return [];
  },

  getManufacturers: async () => {
    return getBrands().map((brand) => ({
      manuId: brand,
      manufacturerId: brand,
      id: brand,
      manuName: brand,
      name: brand,
      manufacturerName: brand,
    }));
  },

  getManufacturersFromCatalog: async () => autoPartsService.getManufacturers(),

  /**
   * Confirm make/model exist in car-api, then return catalog parts.
   * Parts are not vehicle-fitment linked — returns full (or searched) catalog
   * after validating the vehicle against brands/models/generations.
   */
  getPartsForVehicleFromCatalog: async (
    vehicle,
    _maxCategories = 5,
    maxPartsPerCategory = 15
  ) => {
    const make = (vehicle?.make ?? '').trim();
    const model = (vehicle?.model ?? '').trim();
    if (!make && !model) {
      throw new Error('Vehicle make or model is required');
    }

    const brandName = findBrandName(make);
    if (!brandName) {
      throw new Error(`Manufacturer "${make || '?'}" not found in car catalog`);
    }

    const matchedModel = findModel(brandName, model);
    if (!matchedModel) {
      throw new Error(`Model "${model || '?'}" not found for ${brandName}`);
    }

    const yearNum = vehicle?.year ? Number(vehicle.year) : null;
    let generation = null;
    if (yearNum && !Number.isNaN(yearNum)) {
      generation = getGenerationByYear(brandName, matchedModel.name, yearNum);
    }

    const parts = getParts()
      .slice(0, Math.max(20, maxPartsPerCategory * 6))
      .map((p) => {
        const base = toPartResult(p);
        return {
          ...base,
          vehicleLabel: generation
            ? `${brandName} ${matchedModel.name} (${generation.name})`
            : `${brandName} ${matchedModel.name}`,
          vehicleBrand: brandName,
          vehicleModel: matchedModel.name,
          vehicleGeneration: generation?.name || null,
          supplierLinks: getSupplierLinks(base, vehicle),
        };
      });

    return parts;
  },

  getAllSuppliers: async () => {
    return [{ supplierId: 'auto-parts-db', supplierName: 'auto-parts-db' }];
  },

  // ========================================
  // Direct car-api helpers (for testing / UI)
  // ========================================

  getCarBrands: withCatalog(async () => getBrands()),
  getCarBrand: withCatalog(async (name) => getBrand(name)),
  getCarModelsByBrand: withCatalog(async (brand) => getModelsByBrand(brand)),
  getCarGenerations: withCatalog(async (brand, model) => getGenerations(brand, model)),
  getCarGenerationByYear: withCatalog(async (brand, model, year) =>
    getGenerationByYear(brand, model, year)
  ),
  searchCarModels: withCatalog(async (query) => searchModels(query)),
  getAllCatalogParts: withCatalog(async () => getParts().map(toPartResult)),
  searchCatalogParts: withCatalog(async (query) => searchParts(query).map(toPartResult)),
  getCatalogPartSlugs: withCatalog(async () => getPartSlugs()),
};

[
  'searchByArticleNumber',
  'quickArticleSearch',
  'searchByOEMNumber',
  'vinCheck',
  'listModelsByManufacturer',
  'getModels',
  'getVehicleEngineTypes',
  'listProductNames',
  'listArticles',
  'getManufacturers',
  'getManufacturersFromCatalog',
  'getPartsForVehicleFromCatalog',
].forEach((key) => {
  autoPartsService[key] = withCatalog(autoPartsService[key]);
});

export default autoPartsService;
